import { useEffect, useMemo, useRef } from 'react';
import { each, toString } from 'lodash';
import { SYSTEM_EXPAND } from 'common/actions/row-groups/types';
import { PREFERRED_TERMS_ALIAS, STRIKE_PRICE_ALIAS } from 'common/constants/cap-table';
import { RowsGroupsAction, SystemExpandReason } from 'common/reducers/rowsGroups/types';
import { isPendingMeta } from './securityAiPatches';
import { CapTableCell, CapTableCellWithReferences } from '../components/captable-table/CaptableTable.props';

// A visible required cell still lacking a usable value: empty, or failing its
// greaterThan bound (e.g. strike price must be > 0).
export function cellRequiresInput(cell: CapTableCell): boolean {
  if (cell.hidden || !cell.isRequired) {
    return false;
  }
  const value = toString(cell.value ?? '').trim();
  return !value || (cell.greaterThan != null && Number(value) <= cell.greaterThan);
}

// The strike price / preferred terms groups whose cells demand attention on
// load, and why: a pending AI suggestion beats a value still requiring input
// (SCAL-9340). Pure derivation from the parsed cells, so it can re-run on
// every parse.
export const reasonedExpandsFor = (
  capCells: Record<string, CapTableCell> | undefined
): Record<string, SystemExpandReason> => {
  const reasons: Record<string, SystemExpandReason> = {};
  each(capCells, cell => {
    const group = cell.parent ?? (cell.hasSubRows ? cell.alias : undefined);
    if (group !== PREFERRED_TERMS_ALIAS && group !== STRIKE_PRICE_ALIAS) {
      return;
    }
    if (isPendingMeta((cell as CapTableCellWithReferences).aiFieldMetadata)) {
      reasons[group] = 'pending-review';
    } else if (cellRequiresInput(cell)) {
      reasons[group] = reasons[group] ?? 'requires-input';
    }
  });
  return reasons;
};

// SCAL-9340 re-fix: the reasoned load-time expand cannot be a single pass
// latched on the first non-empty parse - cells regularly parse BEFORE the
// securities' ai_finding metadata hydrates, so that pass saw no pending
// suggestions and the groups stayed collapsed. Instead, re-derive the
// group->reason picture from EVERY parse and fire SYSTEM_EXPAND only on a
// transition (a group's reason appearing or changing), so the scan settles
// once per data arrival and re-parses with an unchanged picture fire nothing
// (no expand churn). A fired expand still loses to a persisted or in-session
// USER_COLLAPSE via the precedence guard in useCapTableRowGroups, and it is
// never persisted (it lands as SYSTEM_EXPAND state).
export const useReasonedRowGroupExpands = (
  capCells: Record<string, CapTableCell> | undefined,
  setRowGroups: (action: RowsGroupsAction) => void
): void => {
  const reasons = useMemo(() => reasonedExpandsFor(capCells), [capCells]);
  const firedRef = useRef<Record<string, SystemExpandReason>>({});
  useEffect(() => {
    const fired = firedRef.current;
    Object.entries(reasons).forEach(([row, reason]) => {
      if (fired[row] !== reason) {
        setRowGroups({ type: SYSTEM_EXPAND, row, reason });
      }
    });
    // Groups whose reason DISAPPEARED (all suggestions settled) drop out, so a
    // later new pending run can legitimately re-fire.
    firedRef.current = reasons;
  }, [reasons, setRowGroups]);
};
